import { Autocomplete, Grid } from "@mui/material";
import {
  DataGridPro,
  GridColDef,
  GridColType,
  GridEventListener,
  GridRenderEditCellParams,
  GridValidRowModel,
} from "@mui/x-data-grid-pro";
import { setCurrentDialog, useMaterialUIController } from "context";
import { on } from "events";
import { useMemo, useState } from "react";
import { DialogType } from "./AloveDialog";
import DeactivateDialog from "layouts/itemPageContainer/components/deactivateDialog";

const MDButton = require("components/MDButton/index.js").default;
const MDInput = require("components/MDInput/index.js").default;
const MDTypography = require("components/MDTypography/index.js").default;

interface EditableTableProps<R> {
  rows: R[];
  columns: EditableGridColDef[];
  processRowUpdate: (newRow: R, oldRow: R) => Promise<R> | R;
  onAddRow?: (row: R) => Promise<any>;
  onDelRow?: (row: R) => void;
  modelName: string;
  getRowId?: (row: R) => string;
  onRowOrderChange?: (indexes: any[]) => void;
  getDetailPanelContent?: (params: any) => React.ReactNode;
  getDetailPanelHeight?: (params: any) => number;
  actions?: { label: string; onClick: (row: R) => void }[];
}

export type EditableGridColDef = GridColDef & {
  customType?: "autocomplete";
  multiple?: boolean;
  getOptions?: (row) => { value: any; label: string }[];
};

export const EditableTable = <R extends GridValidRowModel>({
  rows,
  columns,
  processRowUpdate,
  onAddRow,
  onDelRow,
  modelName,
  getRowId,
  onRowOrderChange,
  actions,
  getDetailPanelContent,
  getDetailPanelHeight,
}: EditableTableProps<R>) => {
  const [controller, dispatch] = useMaterialUIController();
  const [newRow, setNewRow] = useState<R | null>(null);
  const [rowToDelete, setRowToDelete] = useState<R | null>(null);

  const changeColumn = (column: EditableGridColDef): EditableGridColDef => {
    column.renderCell = (params) => {
      if (params.row.id.toString().startsWith("new")) {
        if (!params.value) {
          return <MDTypography variant="body2" sx={{ fontStyle: "italic", opacity: 0.5 }} >
            Type to add...
          </MDTypography>
        } 
      }
      return column.valueFormatter?.(params.value as never,params.row,column,params.api as any ) || params.value;
    };
    if (column.customType === "autocomplete") {
      column.renderCell = (params ) => {
        const options = column.getOptions!(params.row);
        if (column.multiple) {
          const values = params.value as any[];
          const selectedOptions = options.filter(o => values?.includes(o.value));
          return selectedOptions.map(o => o.label).join(", ");
        } else {
          const option = options.find(o => o.value == params.value);
          return option?.label || params.value || 
          <MDTypography variant="body2" sx={{ fontStyle: "italic", opacity: 0.5 }} >
            Click to select...
          </MDTypography>
        }
      };
      column.renderEditCell = (params) => {
        const options = column.getOptions!(params.row);
        return (
          <Autocomplete
            options={options}
            getOptionLabel={(o) => o.label}
            getOptionKey={(o) => o.value}
            fullWidth
            multiple={column.multiple}
            disableClearable
            onChange={(e, v: any) =>
              params.api.setEditCellValue({ id: params.id, field: params.field, 
                value: column.multiple ? v?.map(o => o.value) : v?.value
                })
            }
            renderInput={(params) => (
              <MDInput
                {...params}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { border: "none" }, // Removes outline
                    "&:hover fieldset": { border: "none" },
                    "&.Mui-focused fieldset": { border: "none" },
                  },
                }}
              />
            )}
          />
        );
      };
    }
    
    return column;
  };

  const [ids, setIds] = useState(rows.map((r, i) => r.id));
  const reorder = (result) => {
    const { oldIndex, targetIndex } = result;
    const newRows = Array.from(ids);
    const [removed] = newRows.splice(oldIndex, 1);
    newRows.splice(targetIndex, 0, removed);
    setIds(newRows);
    onRowOrderChange?.(newRows);
  };

  const _columns = [
    ...columns.map(changeColumn),
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      width: 120 * ((actions?.length || 0) + 1),
      renderCell: (params) =>
        params.row.id.toString().startsWith("new") ? (
          <MDButton
            variant="text"
            color="primary"
            onClick={() => {
              const { id, ...rest } = params.row;
              onAddRow?.(rest);
              setNewRow(null);
            }}
          >
            Save
          </MDButton>
        ) : (
          [
            ...(actions || []).map((a) => (
              <MDButton variant="text" color="primary" onClick={() => a.onClick(params.row)}>
                {a.label}
              </MDButton>
            )),
            <MDButton
              variant="text"
              color="error"
              onClick={() => {
                setRowToDelete(params.row);
                setCurrentDialog(dispatch, DialogType.DeactivateEntitiy + modelName as any);
              }}
            >
              Delete
            </MDButton>,
          ]
        ),
    },
  ];

  const _processRowUpdate = async (newRow: R, oldRow: R) => {
    if (newRow.id.toString().startsWith("new")) {
      setNewRow(newRow);
      return newRow;
    } else {
      return processRowUpdate(newRow, oldRow);
    }
  };

  const sortByIds = (a: R, b: R) => {
    return ids.indexOf(a.id) - ids.indexOf(b.id);
  };

  return (
    <Grid container flexDirection={"column"} gap={2}>
      <DataGridPro
        rows={rows.sort(sortByIds).concat(newRow ? [newRow] : [])}
        columns={_columns}
        processRowUpdate={_processRowUpdate}
        hideFooter
        getRowId={getRowId}
        onRowOrderChange={onRowOrderChange ? reorder : undefined}
        rowReordering={!!onRowOrderChange}
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={getDetailPanelHeight}
      />
      {!newRow && (
        <Grid
          size={{
            xs: 12,
            md: 4,
          }}
        >
          <MDButton
            variant="outlined"
            color="primary"
            fullWidth
            onClick={() => {
              setNewRow({ id: "new" + rows.length + 1 } as any);
            }}
          >
            + Add {modelName}
          </MDButton>
        </Grid>
      )}
      <DeactivateDialog
        title={`Delete ${modelName}`}
        content={`Are you sure you want to delete this ${modelName}?`}
        confirm="Delete"
        modelName={modelName}
        onSubmit={async () => {
          if (rowToDelete) {
            await onDelRow?.(rowToDelete);
            setRowToDelete(null);
          }
        }}
      />
    </Grid>
  );
};
